import { useConfigStore } from "@/stores/config";
import { REFERENCE_TABLE } from "@/constants/ReferenceTables";
import { CUSTOM_ICONS } from "@/constants/Unicode";

export default function getFeaturesWaterwayData(detail) {
  const configStore = useConfigStore();

  return {
    table: {
      headerRows: 0,
      widths: ["27%", "24%", "26%", "*"],
      body: [
        [
          { text: "F.02 On/Under: ", bold: true },
          {
            text: configStore.getReferenceValue(REFERENCE_TABLE.ON_UNDER, detail?.onUnder),
          },
          { text: "F.03 Feature Type: ", bold: true },
          {
            text: configStore.getReferenceValue(REFERENCE_TABLE.FEATURE_TYPE, detail?.featureType),
          },
        ],
        [
          { text: "WD.01 Stream Name: ", bold: true },
          {
            text: detail?.roadwayName,
          },
          {
            text: "WD.10 Primary Waterway: ",
            bold: true,
          },
          {
            text: detail?.primaryWaterway == "1" ? CUSTOM_ICONS.SQUARE_CHECK : CUSTOM_ICONS.SQUARE,
            style: "customFontAwesome",
          },
        ],
        [
          { text: "WD.02 Stream Class 1: ", bold: true },
          {
            text: configStore.getReferenceValue(REFERENCE_TABLE.STREAM_CLASSIFICATION, detail?.depClass1),
          },
          { text: "WD.11 Vert Clr: ", bold: true },
          {
            text: detail?.minOverVertClearRight + " ft",
          },
        ],
        [
          { text: "WD.03 Stream Class 2: ", bold: true },
          {
            text: configStore.getReferenceValue(REFERENCE_TABLE.STREAM_CLASSIFICATION, detail?.depClass2),
          },
          { text: "WD.12 Max W.S. Elev: ", bold: true },
          {
            text: detail?.maxWaterSurfElev + " ft",
          },
        ],
        [
          { text: "WD.04 Timeframe: ", bold: true },
          {
            text: detail?.depTimeframe,
          },
          { text: "WD.13 Max W.S. Elev Year: ", bold: true },
          {
            text: detail?.maxWaterSurfYear,
          },
        ],
        [
          { text: "WD.05 Stream Class 3: ", bold: true },
          {
            text: configStore.getReferenceValue(REFERENCE_TABLE.STREAM_CLASSIFICATION, detail?.depClass3),
          },
          { text: "WD.14 Design Flood Magn: ", bold: true },
          {
            text: detail?.designFloodMagnitude,
          },
        ],
        [
          { text: "WD.06 Permit Type: ", bold: true },
          {
            text: detail?.depPermitType,
          },
          { text: "WD.15 Design Flood Elev: ", bold: true },
          {
            text: detail?.designFloodElev,
          },
        ],
        [
          { text: "WD.07 Drainage Area: ", bold: true },
          {
            text: detail?.streamDrainArea,
          },
          { text: "WD.16 Design Flood Freq: ", bold: true },
          {
            text: detail?.designFloodFreq + " yrs",
          },
        ],
        [
          {
            text: "WD.08 Fishable: ",
            bold: true,
          },
          {
            text: detail?.fishable == "1" ? CUSTOM_ICONS.SQUARE_CHECK : CUSTOM_ICONS.SQUARE,
            style: "customFontAwesome",
          },
          {
            text: "WD.17 Design Flood Velocity: ",
            bold: true,
          },
          {
            text: detail?.designFloodVel,
          },
        ],
        [
          { text: "WD.09 Waterflow Direction: ", bold: true },
          {
            colSpan: 3,
            text: configStore.getReferenceValue(REFERENCE_TABLE.WATERFLOW_DIRECTION, detail?.waterflowDir),
          },
        ],
        [
          { text: "WD.18 Pollutant Descr: ", bold: true },
          {
            colSpan: 3,
            text: detail?.polutantDesc,
            alignment: "left",
          },
        ],
        [
          { text: "WD.19 Stream Restrict Descr: ", bold: true },
          {
            colSpan: 3,
            text: detail?.streamRestrictDesc,
            alignment: "left",
          },
        ],
        [
          { text: "WD.20 Notes: ", bold: true },
          {
            colSpan: 3,
            text: detail?.notes,
            alignment: "left",
          },
        ],
      ],
    },
    layout: {
      hLineWidth: function (i, node) {
        return i === 0 || i === node.table.body.length ? 2 : 0;
      },
      vLineWidth: function (i, node) {
        return i === 0 || i === node.table.widths.length ? 1 : 0;
      },
      hLineColor: function () {
        return "black";
      },
      vLineColor: function () {
        return "gray";
      },
    },
    unbreakable: true,
    margin: [10, 0, 10, 0],
  };
}

export const getPierInteger = (pier) => {
  // Looks like "Pier .... ##"
  const pierFormat = /^[Pp].*\D(\d+)/;
  // Looks like "P##"
  const pFormat = /^[Pp](\d+)/;
  const pierInt = pierFormat.exec(pier);
  const pInt = pFormat.exec(pier);
  if (pierInt?.length > 1) {
    return Number.parseInt(pierInt[1]);
  } else if (pInt?.length > 1) {
    return Number.parseInt(pInt[1]);
  } else {
    return -1;
  }
};

export const comparePiers = (a, b) => {
  let aNum = getPierInteger(a?.subUnit);
  let bNum = getPierInteger(b?.subUnit);

  // If only one of "a" or "b" does not follow the right format, the logic should be inverted.
  // this pushes entries that do not contain a number to the bottom
  if ((aNum == -1 && bNum != -1) || (aNum != -1 && bNum == -1)) {
    return aNum < bNum ? 1 : -1;
  }
  // If they are both parsable, simply compare
  else if (aNum != -1 && bNum != -1) {
    return aNum < bNum ? -1 : 1;
  }
  // If neither fit the Pier format, just sort them alphabetically
  else {
    return a?.subUnit.toString().toLowerCase().localeCompare(b?.subUnit.toString().toLowerCase());
  }
};

export const floodPlainCustomSort = (a, b) => {
  // Abutments, Piers, Culverts, Walls
  const typeOrder = ["B", "P", "C", "W"];
  const subunitOrder = ["NAB", "FAB", "P", "CIN", "COU", "WNR", "WNL", "WFR", "WFL"];

  let aPosition = typeOrder.indexOf(a?.legacyStructureType);
  let bPosition = typeOrder.indexOf(b?.legacyStructureType);

  // If we find different LegacyStructureTypes, just return immediately
  if (aPosition != bPosition) return aPosition < bPosition ? -1 : 1;

  // If the two subunits are Piers, try to compare their numbers. Otherwise sort them alphabetically
  if (aPosition == 1 && bPosition == 1) {
    return comparePiers(a, b);
  }
  // If the two are the same type, but not Piers, then we need to compare their subunit ordering
  else if (aPosition == bPosition) {
    let aSubunitPosition = subunitOrder.indexOf(a?.subUnit);
    let bSubunitPosition = subunitOrder.indexOf(b?.subUnit);

    // If they're named identically (or don't appear in the list), all we can do is sort alphabetically
    if (aSubunitPosition == bSubunitPosition) {
      return a?.subUnit?.toString().toLowerCase().localeCompare(b?.subUnit?.toString().toLowerCase());
    } else {
      return aSubunitPosition < bSubunitPosition ? -1 : 1;
    }
  }
};
